import { useState } from "react";

import SearchIcon from "@mui/icons-material/Search";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { DeleteShipmentSearch } from "components/Carrier/components/DeleteShipmentSearch";

import { EditLaneModal } from "./EditLaneModal";
import { LaneSelectorItem } from "./LaneSelectorItem";

interface Props {
  open: boolean;
  onClose: VoidFunction;
  lanes: ShipmentSearch[];
  lanesLoading: boolean;
  selectedLaneId: string;
  onLaneSelected: (lane: ShipmentSearch) => void;
  onLaneDeleted: (deletedLaneId: number) => void;
  onLaneUpdated: VoidFunction;
}

export function LaneSelectorModal(props: Props) {
  const {
    open,
    onClose,
    lanes,
    lanesLoading,
    selectedLaneId,
    onLaneSelected,
    onLaneDeleted,
    onLaneUpdated,
  } = props;
  const [searchQuery, setSearchQuery] = useState("");
  const [laneToDelete, setLaneToDelete] = useState<ShipmentSearch | null>(null);
  const [laneToEdit, setLaneToEdit] = useState<ShipmentSearch | null>(null);

  const filteredLanes = lanes.filter((lane) =>
    lane.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleLaneClick = (lane: ShipmentSearch) => {
    onLaneSelected(lane);
    onClose();
  };

  const handleDeleteComplete = () => {
    if (laneToDelete) {
      onLaneDeleted(laneToDelete.id);
    }
    setLaneToDelete(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>My Lanes</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search lanes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
            size="small"
          />
          {lanesLoading && (
            <Stack alignItems="center" sx={{ py: 2 }}>
              <CircularProgress />
            </Stack>
          )}
          {!lanesLoading && filteredLanes.length === 0 && (
            <Typography
              color="text.secondary"
              textAlign="center"
              sx={{ py: 2 }}
            >
              {lanes.length === 0
                ? "No lanes created yet."
                : "No lanes match your search."}
            </Typography>
          )}
          {!lanesLoading &&
            filteredLanes.map((lane) => (
              <LaneSelectorItem
                key={lane.id}
                lane={lane}
                isSelected={lane.id.toString() === selectedLaneId}
                onClick={() => handleLaneClick(lane)}
                onDeleteClick={() => setLaneToDelete(lane)}
                onEditClick={() => setLaneToEdit(lane)}
              />
            ))}
        </DialogContent>
      </Dialog>
      {laneToDelete && (
        <DeleteShipmentSearch
          open
          onClose={() => setLaneToDelete(null)}
          shipmentSearch={laneToDelete}
          reloadSearches={handleDeleteComplete}
        />
      )}
      {laneToEdit && (
        <EditLaneModal
          lane={laneToEdit}
          onClose={() => setLaneToEdit(null)}
          onLaneUpdated={onLaneUpdated}
        />
      )}
    </>
  );
}
